import * as React from 'react';
import { InfoText, InfoTextProps } from '../info/info-text';
import { Validation, ValidationProps, getValidationStyle } from '../validation';
import styles from './input-control-context.scss';
import { LabelConfig } from 'ui/common/types';
import { InputLabel } from 'ui/common/input-label';
import { LabelSpinner } from 'ui/common/label-spinner/label-spinner';
import { MeatballPanelConfiguration } from 'ui/common';

/**
 * Describes how the InputControlContext component is displayed.
 * It wraps describing components in one context component.
 */
export interface InputControlContextProps extends React.PropsWithChildren {
  /**
   * Id of component
   */
  componentId: string;
  /**
   * Set css class of main tag. Default is null.
   */
  className?: string;
  /**
   * Define label of component which is described by [[LabelProps]]. Default is null.  
   */
  label?: LabelConfig;
  /**
   * Define short description of component which is described by [[InfoTextProps]]. Default is null.
   */
  info?: InfoTextProps;
  /**
   * Define long description of component which is described by [[InfoTextProps]]. Default is null.
   */
  infoLong?: InfoTextProps;
  /**
   * Internal. Define validation status of component which is described by [[ValidationProps]]. Default is null.
   */
  validation?: ValidationProps;
  /**
   * Value is true when input is disabled.
   */
  readOnly?: boolean;
  /**
   * Show spinner next to label. 
   * Default is false.
   */
  showSpinnerLabel?: boolean;

  enableMultilineValidation?: boolean;

  meatballPanelConfiguration?: MeatballPanelConfiguration;
  children?: React.ReactNode;
  /**
   * Show AI chat icon before the label.
   * Default is false.
   */
  showAIChatIcon?: boolean;
}

export const InputControlContext: React.FC<InputControlContextProps> = (props) => {
  const {
    validation,
    className = '',
    readOnly,
    componentId,
    label,
    info,
    children,
    infoLong,
    showSpinnerLabel,
    enableMultilineValidation,
    meatballPanelConfiguration,
    showAIChatIcon = false
  } = props;

  const { readOnlyStyle } = styles;

  const level = validation && validation.level;
  const groupClassname = `${className} ${level !== undefined && 'has-' + getValidationStyle(level) || 'valid'}`;
  const meatballPanelClassName = meatballPanelConfiguration?.meatball ? styles.meatballPanel : '';

  return (
    <div className={`form-group ${groupClassname} ${readOnly ? readOnlyStyle : ''}`} id={componentId}>
      <InputLabel {...label} className={meatballPanelClassName} meatballPanelConfiguration={meatballPanelConfiguration}
        showAIChatIcon={showAIChatIcon}>
        <InfoText {...info} />
        {showSpinnerLabel ? <LabelSpinner /> : null}
      </InputLabel>
      {children}
      <Validation {...validation} enableMultilineValidation={enableMultilineValidation} />
      <InfoText {...infoLong} />
    </div>
  );
};

InputControlContext.displayName = 'InputControlContext';
